import { ReferenceEntity } from '../types/reference';
import { formatCitationWithCSL, generateBibliographyWithCSL } from '../services/cslFormatter';

/**
 * Citation Formatting Architecture
 * ================================
 * 
 * This module provides the main citation formatting interface for the Library app.
 * 
 * ## Overview
 * 
 * We use citation-js library (via cslFormatter.ts) for standards-compliant CSL formatting.
 * This gives us access to thousands of citation styles and proper academic formatting.
 * 
 * ## Module Responsibilities
 * 
 * - **citationFormatter.ts** (this file): Main interface, utilities, type definitions
 * - **cslFormatter.ts**: CSL implementation using citation-js library
 * - **citationConfig.ts**: Configuration management for citation styles
 * 
 * ## Functions
 * 
 * - `formatCitation()`: Sync fallback for inline citations (used during typing)
 * - `formatCitationAsync()`: Proper CSL formatting (preferred when async is OK)
 * - `formatBibliographyEntry()`: Format single reference for bibliography
 * - `parseAuthorName()`: Utility to parse author strings into structured names
 * 
 * ## Style Support
 * 
 * Supports all CSL styles including: APA, MLA, Chicago, Harvard, Vancouver, Nature,
 * Science, IEEE, and many more. Custom CSL files can be loaded via loadCustomCSLStyle().
 * 
 * ## Migration Note
 * 
 * Previously had manual formatting implementations. These have been replaced with
 * CSL-based formatting for better standards compliance and broader style support.
 */

export type CitationStyle = 
  | 'author-year' 
  | 'numeric' 
  | 'apa' 
  | 'harvard1'
  | 'vancouver'
  | 'mla' 
  | 'chicago-note-bibliography'
  | 'ieee'
  | 'nature'
  | 'science'
  | string; // Allow any CSL style name

interface AuthorName {
  family: string;  // Last name
  given?: string;  // First name
  suffix?: string; // Jr., Sr., III, etc.
}

/**
 * Parse author string into structured name
 * Handles formats:
 * - "LastName, FirstName Middle" (most common in metadata)
 * - "FirstName LastName"
 * - "LastName"
 */
export function parseAuthorName(authorString: string): AuthorName {
  const trimmed = authorString.trim();
  
  // Check for comma format: "LastName, FirstName"
  if (trimmed.includes(',')) {
    const [family, given] = trimmed.split(',').map(s => s.trim());
    return { family, given };
  }
  
  // Check for suffix: "LastName Jr." or "LastName III"
  const suffixMatch = trimmed.match(/^(.+?)\s+(Jr\.?|Sr\.?|III|II|IV)$/i);
  if (suffixMatch) {
    const [, rest, suffix] = suffixMatch;
    const parts = rest.trim().split(' ');
    return {
      family: parts[parts.length - 1],
      given: parts.slice(0, -1).join(' ') || undefined,
      suffix
    };
  }
  
  // Default: "FirstName LastName" format
  const parts = trimmed.split(' ');
  if (parts.length === 1) {
    return { family: parts[0] };
  }
  
  return {
    family: parts[parts.length - 1],
    given: parts.slice(0, -1).join(' ')
  };
}

/**
 * Map legacy style names to CSL style names
 */
function mapStyleToCSL(style: CitationStyle): string {
  const styleMap: Record<string, string> = {
    'author-year': 'apa',
    'numeric': 'ieee',
    'chicago': 'chicago-note-bibliography'
  };
  
  return styleMap[style] || style;
}

/**
 * Main citation formatting function - uses CSL formatter
 * This is the primary function to use for in-text citations
 * 
 * @param references - Array of references to cite
 * @param style - Citation style (e.g., 'apa', 'mla', 'chicago')
 * @returns Formatted citation string (synchronous fallback if CSL fails)
 */
export function formatCitation(
  references: ReferenceEntity[],
  style: CitationStyle = 'author-year'
): string {
  // CSL formatter is async, but we need sync for inline citations
  // Use a basic fallback format immediately, then upgrade to CSL if possible
  
  const cslStyle = mapStyleToCSL(style);
  
  // Basic fallback format (author-year)
  const citations = references.map(ref => {
    const authors = ref.skeleton?.authors || [];
    const year = ref.skeleton?.year || 'n.d.';
    
    if (authors.length === 0) {
      return `Unknown, ${year}`;
    }
    
    const parsedAuthors = authors.map(parseAuthorName);
    
    if (parsedAuthors.length === 1) {
      return `${parsedAuthors[0].family}, ${year}`;
    }
    
    if (parsedAuthors.length === 2) {
      return `${parsedAuthors[0].family} & ${parsedAuthors[1].family}, ${year}`;
    }
    
    return `${parsedAuthors[0].family} et al., ${year}`;
  });
  
  return `(${citations.join('; ')})`;
}

/**
 * Async version that uses proper CSL formatting
 * Use this when you can handle async (e.g., in components, not in real-time editor)
 */
export async function formatCitationAsync(
  references: ReferenceEntity[],
  style: CitationStyle = 'author-year'
): Promise<string> {
  const cslStyle = mapStyleToCSL(style);
  return formatCitationWithCSL(references, cslStyle, { format: 'text', type: 'citation' });
}

/**
 * Generate full bibliography entry for a reference
 * Use this for bibliography sections
 * 
 * @param ref - Reference to format
 * @param style - Bibliography style (e.g., 'apa', 'mla')
 * @returns Formatted bibliography entry (HTML)
 */
export async function formatBibliographyEntry(
  ref: ReferenceEntity,
  style: CitationStyle = 'apa'
): Promise<string> {
  const cslStyle = mapStyleToCSL(style);
  return generateBibliographyWithCSL([ref], cslStyle, 'html');
}

/**
 * Synchronous fallback bibliography formatter
 * Use only when async is not possible
 */
export function formatBibliographyEntrySync(
  ref: ReferenceEntity,
  style: CitationStyle = 'apa'
): string {
  const authors = ref.skeleton?.authors || [];
  const year = ref.skeleton?.year || 'n.d.';
  const title = ref.skeleton?.title || ref.name || 'Untitled';
  const journal = ref.skeleton?.journal;
  const volume = ref.skeleton?.volume;
  const issue = ref.skeleton?.issue;
  const pages = ref.skeleton?.pages;
  const doi = ref.skeleton?.doi;
  
  // Basic APA format fallback
  const authorList = authors.join(', ');
  let entry = `${authorList || 'Unknown'}. (${year}). ${title}.`;
  
  if (journal) {
    entry += ` *${journal}*`;
    if (volume) entry += `, ${volume}`;
    if (issue) entry += `(${issue})`;
    if (pages) entry += `, ${pages}`;
    entry += '.';
  }
  
  if (doi) {
    entry += ` https://doi.org/${doi}`;
  }
  
  return entry;
}

/**
 * Re-export CSL utilities for convenience
 */
export { 
  getAvailableStyles,
  loadCustomCSLStyle
} from '../services/cslFormatter';
